import React, { useEffect } from 'react';
import { X, Compass, Zap, Sliders, Keyboard } from 'lucide-react';

interface ShortcutGroup {
  category: string;
  icon: React.ElementType;
  items: {
    keys: string[];
    description: string;
    action?: () => void;
  }[];
}

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsModal({ isOpen, onClose }: KeyboardShortcutsModalProps) {

  // Handle Esc key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const groups: ShortcutGroup[] = [
    {
      category: 'Navigation Sequences',
      icon: Compass,
      items: [
        { keys: ['G', 'D'], description: 'Jump to Overview Dashboard' },
        { keys: ['G', 'M'], description: 'Jump to My Assigned Tasks' },
        { keys: ['G', 'P'], description: 'Jump to Projects Directory' },
        { keys: ['G', 'T'], description: 'Jump to Tasks Kanban Board' },
        { keys: ['G', 'W'], description: 'Jump to Time Tracking' },
        { keys: ['G', 'U'], description: 'Jump to Team Members' },
        { keys: ['G', 'A'], description: 'Jump to Activity & Audit Logs' },
        { keys: ['G', 'R'], description: 'Jump to Reports & Analytics' },
        { keys: ['G', 'C'], description: 'Jump to Clients (Owner / Manager)' },
        { keys: ['G', 'F'], description: 'Jump to Finance (Owner Only)' },
        { keys: ['G', 'S'], description: 'Jump to Settings & API' },
      ],
    },
    {
      category: 'Workspace Actions',
      icon: Zap,
      items: [
        { keys: ['Ctrl', 'K'], description: 'Open Quick Search modal' },
        { keys: ['Ctrl', '/'], description: 'Toggle Sidebar collapse' },
        { keys: ['?'], description: 'Open this Keyboard Shortcuts cheat sheet' },
        { keys: ['Esc'], description: 'Dismiss active dialog or search overlay' },
      ],
    },
    {
      category: 'Productivity & Display',
      icon: Sliders,
      items: [
        { keys: ['T'], description: 'Toggle Dark / Light appearance theme' },
      ],
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-fast"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl border border-brand-border bg-surface shadow-2xl overflow-hidden text-brand-ink transition-all animate-in zoom-in-95 duration-fast"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-brand-border px-6 py-4 bg-slate-50/70 dark:bg-[#0E1A18]">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-brand-tint/60 text-brand">
              <Keyboard className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-brand-ink">Keyboard Shortcuts</h2>
              <p className="text-xs text-brand-muted">Power-user key combinations for lightning-fast agency navigation</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-brand-muted hover:text-brand-ink hover:bg-brand-tint/40 transition-colors"
            title="Close (Esc)"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Shortcuts Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-6">
          {groups.map((group) => {
            const Icon = group.icon;
            return (
              <div key={group.category} className="space-y-3">
                <div className="flex items-center space-x-2 text-xs font-bold uppercase tracking-wider text-brand-muted">
                  <Icon className="h-4 w-4 text-brand" />
                  <span>{group.category}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {group.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2.5 rounded-xl border border-brand-border/60 bg-surface hover:border-brand/40 transition-colors"
                    >
                      <span className="text-xs text-brand-ink font-medium pr-2">{item.description}</span>
                      <div className="flex items-center space-x-1 shrink-0">
                        {item.keys.map((key, kIdx) => (
                          <kbd
                            key={kIdx}
                            className="inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 text-[11px] font-mono font-bold text-brand-ink bg-brand-tint/60 border border-brand-border rounded-md shadow-xs"
                          >
                            {key}
                          </kbd>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between border-t border-brand-border px-6 py-3 bg-slate-50/50 dark:bg-[#0E1A18] text-xs text-brand-muted">
          <span>Tip: Press <kbd className="px-1.5 py-0.5 font-mono text-[10px] font-bold bg-brand-tint/60 rounded">G</kbd> followed by any destination key anywhere.</span>
          <button
            onClick={onClose}
            className="px-3 py-1 rounded-lg bg-brand-tint/50 text-brand font-semibold text-xs hover:bg-brand-tint transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
